import {
  useMutation,
  useQuery,
  useQueryClient,
  queryOptions,
} from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router';
import config from 'constants/apiConfig';
import { parseError } from 'utils/parseTypedErrorMessage';
import { useActualIncidentId } from '../commonHooks';

export type RosterResource = {
  id: string;
  resourceInIncidentId: string;
  incidentId: string;
  resourceSetId: string;
  reportsToPosition: string;
  resourceSet: {
    id: string;
    name: string;
    kind: string;
    type: string | null;
    pointOfContact: string | null;
  };
  pointOfContactUser: {
    userGuid: string;
    userName: string | null;
    emailAddress: string | null;
  } | null;
};

export type AddRosterResourcePayload = {
  resourceInIncidentId: string;
  pointOfContactUserGuid: string;
  positionId: string;
  isDraft?: boolean;
};

export const rosterResourcesKeys = {
  base: ['rosterResources'] as const,
  byIncident: (incidentId: string) =>
    [...rosterResourcesKeys.base, incidentId] as const,
  byIncidentAndRoster: (incidentId: string, isDraft: boolean) =>
    [...rosterResourcesKeys.byIncident(incidentId), isDraft] as const,
  options: (incidentId: string) =>
    [...rosterResourcesKeys.byIncident(incidentId), 'options'] as const,
} as const;

export const rosterResourcesQueryOptions = (
  incidentId: string,
  isDraft: boolean
) =>
  queryOptions({
    queryKey: rosterResourcesKeys.byIncidentAndRoster(incidentId, isDraft),
    queryFn: async () => {
      const { data } = await axios.get<RosterResource[]>(
        config.apiGateway.rosterResources(incidentId),
        { params: { isDraft } }
      );
      return data ?? [];
    },
    enabled: !!incidentId,
  });

export const useRosterResources = () => {
  const incidentId = useActualIncidentId();
  const [searchParams] = useSearchParams();
  const isDraft = searchParams.get('mode') === 'draft';
  return useQuery(rosterResourcesQueryOptions(incidentId, isDraft));
};

export const useAddResourceToRoster = () => {
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const isDraft = searchParams.get('mode') === 'draft';

  return useMutation({
    mutationFn: async (payload: AddRosterResourcePayload) => {
      const { data } = await axios.post<RosterResource>(
        config.apiGateway.rosterResources(incidentId),
        { ...payload, isDraft: payload.isDraft ?? isDraft }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: rosterResourcesKeys.byIncident(incidentId),
      });
      toast.success('Resource added to roster');
    },
    onError: (error: unknown) => {
      toast.error(parseError(error) ?? 'Failed to add resource to roster');
    },
  });
};

export const useRemoveResourceFromRoster = () => {
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const isDraft = searchParams.get('mode') === 'draft';

  return useMutation({
    mutationFn: async (resourceInIncidentId: string) => {
      const { data } = await axios.delete<RosterResource>(
        config.apiGateway.rosterResource(incidentId, resourceInIncidentId),
        { params: { isDraft } }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: rosterResourcesKeys.byIncident(incidentId),
      });
      toast.success('Resource removed from roster');
    },
    onError: (error: unknown) => {
      toast.error(parseError(error) ?? 'Failed to remove resource from roster');
    },
  });
};

type RosterResourceOptionBase = {
  key: string;
  id: string | null;
  label: string | null;
  positionName: string | null;
  detail: string | null;
  resourceInIncidentId: string | null;
};

export type RosterResourceOption =
  | (RosterResourceOptionBase & { kind: 'personnel'; positionIds: string[] })
  | (RosterResourceOptionBase & { kind: 'position'; positionId: string });

export const useRosterResourceOptions = () => {
  const incidentId = useActualIncidentId();

  return useQuery({
    queryKey: rosterResourcesKeys.options(incidentId),
    queryFn: async () => {
      const { data } = await axios.get<RosterResourceOption[]>(
        config.apiGateway.rosterResourceOptions(incidentId)
      );
      return data ?? [];
    },
    enabled: !!incidentId,
  });
};
